import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Alert,
  AlertIcon,
  ModalBody,
  Box,
} from '@chakra-ui/react';

import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getClassAction,
  updateClassAction,
} from 'store/modules/class/class-action';
import { classActions } from 'store/modules/class/class-slice';
import { ReducerStatus } from 'utils/constants';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { format, parseJSON } from 'date-fns';
import useToastNotif from 'hooks/useToastNotif';
import { TOAST_ERROR } from 'utils/constants/toast';
import ResultModal from 'components/common/modals/ResultModal';
import ClassForm from '../ClassForm';

function UpdateClassMd() {
  const dispatch = useDispatch();
  const { generateToastNotif } = useToastNotif();

  const classObject = useSelector((state) => state.class.class);
  const getClass = useSelector((state) => state.class.getClass);
  const updateClass = useSelector((state) => state.class.updateClass);

  const [formInputs, setFormInputs] = useState({
    name: '',
    description: '',
    learningPlan: null,
    emails: [], // Initialize as empty array for consistency
    isPublished: '',
    costCenter: null,
    dateStart: '',
    dateEnd: '',
    thumbnailPath: '',
  });
  const [resetTrigger, setResetTrigger] = useState(0);

  const loadLearningPlans = debounce((inputValue, callback) => {
    axios
      .get(`/admin/learning-plans?query=${inputValue.trim()}&pageSize=${20}`)
      .then((response) => {
        const options = response.data.data?.map((item) => ({
          label: item.name,
          value: item.id,
        }));

        if (formInputs.learningPlan) {
          if (!options.some((option) => option.value === formInputs.value))
            options.push(formInputs);
        }

        callback(options);
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  const formValidation = Yup.object({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().max(1000).required('Required'),
    learningPlan: Yup.object(),
    costCenter: Yup.string().nullable().max(20),
    dateStart: Yup.date().required('Required'),
    dateEnd: Yup.date()
      .min(
        Yup.ref('dateStart'),
        'End date must be greater than or equal to start date'
      )
      .required('Required'),
  });

  const handleToggleUpdateModal = (isOpen) => {
    dispatch(
      classActions.setUpdateClass({
        modal: isOpen,
      })
    );
  };

  const handleSubmitForm = (submittedForm) => {
    const formData = new FormData();

    Object.entries(submittedForm).forEach(([key, value]) => {
      if (key !== 'file' && key !== 'emails' && key !== 'learningPlanId')
        formData.append(key, value);
    });

    if (submittedForm.learningPlan)
      formData.append('LearningPlanId', submittedForm.learningPlan.value);

    submittedForm.emails.forEach((group) =>
      formData.append('OwnerGroups', group)
    );

    if (submittedForm.file && submittedForm.file.name) {
      formData.append('Thumbnail', submittedForm.file, submittedForm.file.name);
    }

    dispatch(updateClassAction({ id: updateClass.id, classData: formData }));
  };

  const handleResultButton = () => {
    dispatch(
      classActions.setUpdateClass({
        status: ReducerStatus.IDLE,
      })
    );
    if (updateClass.status === ReducerStatus.SUCCEEDED) {
      setResetTrigger((prev) => prev + 1);
      setFormInputs({
        name: '',
        description: '',
        learningPlan: null,
        emails: [],
        isPublished: '',
        costCenter: null,
        dateStart: '',
        dateEnd: '',
        thumbnailPath: '',
      });
      dispatch(
        classActions.setUpdateClass({
          modal: false,
          id: null,
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
    }
  };

  useEffect(() => {
    if (updateClass.modal && updateClass.id) {
      dispatch(getClassAction({ id: updateClass.id }));
    }
  }, [dispatch, updateClass.id, updateClass.modal]);

  useEffect(() => {
    // Only set form inputs once when the modal opens and classObject is loaded
    if (
      classObject &&
      updateClass.modal &&
      getClass.status === ReducerStatus.SUCCEEDED
    ) {
      let formEmails = [];
      const parsedEmails = JSON.parse(classObject?.ownerGroups);
      if (
        Array.isArray(parsedEmails) &&
        parsedEmails.every((group) => typeof group === 'string')
      ) {
        formEmails = parsedEmails;
      }

      setFormInputs({
        name: classObject.name,
        description: classObject.description,
        learningPlan: classObject.learningPlan
          ? {
              value: classObject.learningPlan?.id,
              label: classObject.learningPlan?.name,
            }
          : null,
        learningPlanId: classObject.learningPlanId,
        isPublished: classObject.isPublished.toString(),
        costCenter: classObject.costCenter,
        dateStart: format(parseJSON(classObject.dateStart), 'yyyy-MM-dd'),
        dateEnd: format(parseJSON(classObject.dateEnd), 'yyyy-MM-dd'),
        thumbnailPath: classObject.thumbnailPath,
        emails: formEmails,
      });
    }
  }, [classObject, updateClass.modal, getClass.status]);

  useEffect(() => {
    if (getClass.status === ReducerStatus.FAILED) {
      generateToastNotif(
        TOAST_ERROR,
        getClass.error?.data?.status || '404',
        getClass.error?.data?.title || 'Not Found',
        true,
        false
      );

      dispatch(
        classActions.setUpdateClass({
          modal: false,
          id: null,
        })
      );
    }
  }, [getClass, generateToastNotif, dispatch]);

  return (
    <Box>
      {updateClass.status === ReducerStatus.SUCCEEDED ||
      updateClass.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={updateClass.modal}
          onSubmit={handleResultButton}
          isError={updateClass.status === ReducerStatus.FAILED}
          title={
            updateClass.status === ReducerStatus.SUCCEEDED
              ? 'Class Updated'
              : null
          }
          message={
            updateClass.status === ReducerStatus.SUCCEEDED
              ? 'The class has been updated successfully.'
              : updateClass.error?.data?.title || 'Server Error'
          }
          errorObject={updateClass.error?.data?.errors}
        />
      ) : (
        <Modal
          size="2xl"
          isOpen={updateClass.modal}
          onClose={() => handleToggleUpdateModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Class</ModalHeader>
            <ModalCloseButton />
            <ModalBody my={3}>
              {getClass.status === ReducerStatus.LOADING ? (
                <Alert my={4} status="loading" variant="left-accent">
                  <AlertIcon />
                  Fetching data please wait
                </Alert>
              ) : (
                <ClassForm
                  flex={1}
                  resetTrigger={resetTrigger}
                  status={updateClass.status}
                  formInputs={formInputs}
                  formValidation={formValidation}
                  onSubmitHandler={handleSubmitForm}
                  loadLearningPlans={loadLearningPlans}
                  isEditMode
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default UpdateClassMd;
